import { Sparkles } from "lucide-react";

import { Badge } from "@runcheetah/ui";
import { TaskStatusBadge } from "@/pages/social-post-planner/components/task-status-badge";
import type {
  SocialPostGenerationTask,
  SocialPostGenerationTaskDetail,
} from "@/pages/social-post-planner/types";
import { formatDate, formatDuration } from "@/pages/social-post-planner/utils";

import type { CampaignReviewPlannedPost } from "./types";

type PlannedPostAttempt = NonNullable<CampaignReviewPlannedPost["output"]["latestAttempt"]>;
type PlannedPostCompleted = NonNullable<CampaignReviewPlannedPost["output"]["latestCompleted"]>;

export function MetaBadge({ label, value }: { label: string; value: string }) {
  return (
    <Badge
      variant="outline"
      className="rounded-full border-border/80 bg-card/90 px-3 py-1.5 text-[11px] text-foreground/78"
    >
      <span className="font-semibold text-foreground/52">{label}:</span>&nbsp;{value}
    </Badge>
  );
}

export function ReviewStatusNotice({
  latestAttempt,
  latestCompleted,
}: {
  latestAttempt: PlannedPostAttempt | null;
  latestCompleted: PlannedPostCompleted | null;
}) {
  if (latestAttempt?.status === "failed" && latestCompleted) {
    return (
      <div className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
        The latest regeneration attempt failed. Review stays focused on the most recent successful
        creative.
      </div>
    );
  }

  if (latestAttempt?.status === "failed") {
    return (
      <div className="rounded-2xl border border-destructive/20 bg-destructive/5 px-4 py-3 text-sm text-destructive">
        {latestAttempt.lastError ?? "The latest attempt failed without a detailed error message."}
      </div>
    );
  }

  return null;
}

export function CurrentAttemptSummary({
  selectedTask,
  selectedTaskDetails,
}: {
  selectedTask: SocialPostGenerationTask | null;
  selectedTaskDetails: SocialPostGenerationTaskDetail | null;
}) {
  if (!selectedTask) {
    return <p className="text-sm text-foreground/62">No attempts yet for this planned post.</p>;
  }

  const unpricedEventCount = selectedTaskDetails?.llmCosts.unpricedEventCount ?? 0;

  return (
    <div className="space-y-3 rounded-2xl border border-border/60 bg-background/80 px-4 py-3">
      <div className="flex flex-wrap items-center gap-2">
        <TaskStatusBadge status={selectedTaskDetails?.status ?? selectedTask.status} />
        <span className="text-sm text-foreground/62">
          {formatDate(selectedTaskDetails?.updatedAt ?? selectedTask.updatedAt)}
        </span>
      </div>
      <div className="grid gap-2 text-sm text-foreground/72">
        <div>
          <span className="font-medium text-foreground">Duration:</span>{" "}
          {formatDuration(
            selectedTask.createdAt,
            selectedTaskDetails?.updatedAt ?? selectedTask.updatedAt,
          )}
        </div>
        <div>
          <span className="font-medium text-foreground">LLM Cost:</span> $
          {selectedTaskDetails?.llmCosts.totalCostUsd.toFixed(3) ?? "--"}
          {unpricedEventCount > 0 ? (
            <span className="text-amber-700">
              {" "}
              ({unpricedEventCount} unpriced event{unpricedEventCount === 1 ? "" : "s"})
            </span>
          ) : null}
        </div>
      </div>
    </div>
  );
}

export function EmptyReviewState({ postStatus }: { postStatus: string }) {
  const copy =
    postStatus === "generating" || postStatus === "scheduled"
      ? {
          title: "Creative in progress",
          description: "The latest render will appear here when this planned post completes.",
        }
      : {
          title: "No creative yet",
          description: "Generate an attempt when you are ready to move this planned post forward.",
        };

  return (
    <div className="flex min-h-[28rem] flex-col items-center justify-center gap-3 px-6 py-12 text-center">
      <Sparkles className="size-8 text-foreground/40" aria-hidden="true" />
      <div className="space-y-1">
        <div className="font-medium text-foreground">{copy.title}</div>
        <p className="max-w-md text-sm text-foreground/62">{copy.description}</p>
      </div>
    </div>
  );
}

export function DetailBlock({ label, value }: { label: string; value: string }) {
  return (
    <div className="space-y-1">
      <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-foreground/48">
        {label}
      </div>
      <p className="text-[15px] leading-7 text-foreground/82">{value}</p>
    </div>
  );
}
